import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, RefreshCw, ChevronDown, CheckCircle, Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
    getBalance,
    getCachedBalance,
    setCachedBalance,
    getSpotHoldings,
    getLastKnownPrices,
    getSpotCostBasis,
    addSpotHolding,
    deductSpotHolding,
    applyLocalBalanceChange,
} from '../../services/walletService'
import { useToast } from '../../context/ToastContext'
import Layout from '../../components/Layout/Layout'
import '../../styles/dashboard.css'

// ─── Coin meta ──────────────────────────────────────────────────────────────
const COIN_LOGOS: Record<string, string> = {
    USDT: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',
    BTC:  'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
    ETH:  'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
    SOL:  'https://assets.coingecko.com/coins/images/4128/small/solana.png',
    BNB:  'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
    XRP:  'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.webp',
    DOGE: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
    ADA:  'https://assets.coingecko.com/coins/images/975/small/cardano.png',
    AVAX: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
    LINK: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
    DOT:  'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',
    LTC:  'https://assets.coingecko.com/coins/images/2/small/litecoin.png',
    UNI:  'https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png',
    ATOM: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png',
    TRX:  'https://assets.coingecko.com/coins/images/1094/small/tron-logo.png',
    NEAR: 'https://assets.coingecko.com/coins/images/10365/small/near.jpg',
    SUI:  'https://assets.coingecko.com/coins/images/26375/small/sui-ocean-square.png',
    APT:  'https://assets.coingecko.com/coins/images/26455/small/aptos_round.png',
    OP:   'https://assets.coingecko.com/coins/images/25244/small/Optimism.png',
    ARB:  'https://assets.coingecko.com/coins/images/16547/small/photo_2023-03-29_21.47.00.jpeg',
    SHIB: 'https://assets.coingecko.com/coins/images/11939/small/shiba.png',
    PEPE: 'https://assets.coingecko.com/coins/images/29850/small/pepe-token.jpeg',
    TON:  'https://assets.coingecko.com/coins/images/17980/small/ton_symbol.png',
}

const COIN_NAMES: Record<string, string> = {
    USDT: 'Tether', BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana',
    BNB: 'BNB', XRP: 'Ripple', DOGE: 'Dogecoin', ADA: 'Cardano',
    AVAX: 'Avalanche', LINK: 'Chainlink', DOT: 'Polkadot', LTC: 'Litecoin',
    UNI: 'Uniswap', ATOM: 'Cosmos', TRX: 'TRON', NEAR: 'NEAR Protocol',
    SUI: 'Sui', APT: 'Aptos', OP: 'Optimism', ARB: 'Arbitrum',
    SHIB: 'Shiba Inu', PEPE: 'Pepe', TON: 'Toncoin',
}

const ALL_COINS = ['USDT', 'BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX',
    'LINK', 'DOT', 'LTC', 'UNI', 'ATOM', 'TRX', 'NEAR', 'SUI', 'APT', 'OP', 'ARB',
    'SHIB', 'PEPE', 'TON']

// Human-readable number — never scientific notation
function fmt(n: number): string {
    if (n === 0) return '0'
    if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    // Small numbers: show enough significant digits, max 8 decimals
    const decimals = Math.min(8, Math.max(2, -Math.floor(Math.log10(n)) + 3))
    return n.toFixed(decimals).replace(/\.?0+$/, '')
}

function CoinLogo({ symbol, size = 28 }: { symbol: string; size?: number }) {
    const src = COIN_LOGOS[symbol]
    if (src) return <img src={src} alt={symbol} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    return <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--ui-bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, color: 'var(--ui-text-secondary)', flexShrink: 0 }}>{symbol.charAt(0)}</div>
}

// ─── Coin Picker Sheet ───────────────────────────────────────────────────────
function CoinPicker({ open, onClose, onSelect, exclude, holdings }: {
    open: boolean; onClose: () => void; onSelect: (c: string) => void; exclude: string; holdings: Record<string, number>
}) {
    const [q, setQ] = useState('')
    const filtered = ALL_COINS.filter(c => c !== exclude && (c.includes(q.toUpperCase()) || (COIN_NAMES[c] || '').toLowerCase().includes(q.toLowerCase())))

    useEffect(() => { if (open) setQ('') }, [open])

    if (!open) return null
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ui-bg-card)', borderRadius: '20px 20px 0 0', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
                {/* Handle */}
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0' }}>
                    <div style={{ width: 36, height: 4, borderRadius: 99, background: 'var(--ui-bg-elevated)' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 10px' }}>
                    <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 16 }}>Select Coin</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--ui-text-muted)', cursor: 'pointer', display: 'flex' }}><X size={20} /></button>
                </div>
                {/* Search */}
                <div style={{ margin: '0 16px 12px', background: 'var(--ui-bg-primary)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
                    <Search size={15} color="#71717a" />
                    <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search coin..." style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--ui-text-primary)', fontSize: 14, fontFamily: 'inherit' }} />
                </div>
                {/* List */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {filtered.map(coin => {
                        const held = holdings[coin] || 0
                        return (
                            <div key={coin} onClick={() => { onSelect(coin); onClose(); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #1C1C2C' }}>
                                <CoinLogo symbol={coin} size={36} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 14 }}>{coin}</div>
                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>{COIN_NAMES[coin] || coin}</div>
                                </div>
                                {held > 0 && <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: 'var(--ui-text-secondary)', fontSize: 13, fontWeight: 600 }}>{fmt(held)}</div>
                                    <div style={{ color: 'var(--ui-text-secondary)', fontSize: 11 }}>Balance</div>
                                </div>}
                            </div>
                        )
                    })}
                    {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: 'var(--ui-text-muted)', fontSize: 13 }}>No coins found</div>}
                </div>
            </div>
        </div>
    )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ConvertCrypto() {
    const navigate = useNavigate()
    const { toast } = useToast()

    const [fromCoin, setFromCoin] = useState('USDT')
    const [toCoin, setToCoin]     = useState('BTC')
    const [fromAmount, setFromAmount] = useState('')
    const [toAmount, setToAmount]     = useState('')
    const [rate, setRate]             = useState<number | null>(null)
    const [rateLoading, setRateLoading] = useState(false)
    const [submitting, setSubmitting]   = useState(false)
    const [success, setSuccess]         = useState(false)
    const [lastResult, setLastResult]   = useState<{ fromAmt: number; toAmt: number; from: string; to: string } | null>(null)
    const [pickerFor, setPickerFor]     = useState<'from' | 'to' | null>(null)
    const [swapping, setSwapping]       = useState(false)
    const [usdtBalance, setUsdtBalance] = useState(() => getCachedBalance())
    const [balanceLoading, setBalanceLoading] = useState(true)
    const rateTimer = useRef<any>(null)

    // Fetch the live, authoritative USDT balance on mount — never trust only the cache.
    useEffect(() => {
        getBalance()
            .then(data => {
                const b = data.balance || 0
                setUsdtBalance(b)
                setCachedBalance(b)
            })
            .catch(() => { /* keep cached value as fallback */ })
            .finally(() => setBalanceLoading(false))
    }, [])

    // Build holdings map (USDT = spot balance)
    const spotHoldings = getSpotHoldings()
    const lastPrices   = getLastKnownPrices()
    // const costBasis = getSpotCostBasis()

    const holdings: Record<string, number> = { USDT: usdtBalance, ...spotHoldings }
    const fromBalance = holdings[fromCoin] || 0

    // ── Fetch live rate ──────────────────────────────────────────────────────
    const fetchRate = async (from: string, to: string) => {
        setRateLoading(true)
        try {
            // Both are non-USDT: get price in USDT for each then divide
            const getUsdtPrice = async (coin: string): Promise<number> => {
                if (coin === 'USDT') return 1
                // Try last known prices first
                const cached = lastPrices[`${coin}/USDT`] || lastPrices[`${coin}USDT`]
                if (cached && cached > 0) return cached
                const res = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${coin}USDT`)
                if (!res.ok) throw new Error('No price')
                const d = await res.json()
                return parseFloat(d.price)
            }
            const [fromUsd, toUsd] = await Promise.all([getUsdtPrice(from), getUsdtPrice(to)])
            setRate(fromUsd / toUsd)
        } catch {
            // fallback: try direct pair
            try {
                const sym = `${from}${to}`
                const res = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${sym}`)
                const d = await res.json()
                setRate(parseFloat(d.price))
            } catch { setRate(null) }
        } finally { setRateLoading(false) }
    }

    useEffect(() => {
        clearTimeout(rateTimer.current)
        rateTimer.current = setTimeout(() => fetchRate(fromCoin, toCoin), 300)
        return () => clearTimeout(rateTimer.current)
    }, [fromCoin, toCoin])

    // ── Sync amounts ─────────────────────────────────────────────────────────
    const handleFromChange = (v: string) => {
        setFromAmount(v)
        if (rate && v && !isNaN(parseFloat(v))) {
            setToAmount((parseFloat(v) * rate).toFixed(8).replace(/\.?0+$/, '') || '')
        } else { setToAmount('') }
    }

    const handleToChange = (v: string) => {
        setToAmount(v)
        if (rate && v && !isNaN(parseFloat(v))) {
            setFromAmount((parseFloat(v) / rate).toFixed(8).replace(/\.?0+$/, '') || '')
        } else { setFromAmount('') }
    }

    useEffect(() => {
        if (rate && fromAmount && !isNaN(parseFloat(fromAmount))) {
            setToAmount((parseFloat(fromAmount) * rate).toFixed(8).replace(/\.?0+$/, '') || '')
        }
    }, [rate])

    const handleSwap = () => {
        setSwapping(true)
        setTimeout(() => {
            const prevFrom = fromCoin; const prevTo = toCoin
            setFromCoin(prevTo); setToCoin(prevFrom)
            setFromAmount(''); setToAmount('')
            setSwapping(false)
        }, 200)
    }

    const setPercent = (pct: number) => {
        if (!fromBalance) return
        const val = (fromBalance * pct / 100)
        const str = val < 0.0001 ? val.toFixed(8) : val.toFixed(fromBalance < 1 ? 6 : 2)
        handleFromChange(str)
    }

    const handleConvert = async () => {
        const fAmt = parseFloat(fromAmount)
        const tAmt = parseFloat(toAmount)
        if (!fAmt || !tAmt || isNaN(fAmt) || isNaN(tAmt)) { toast('Enter an amount', 'warning'); return }
        if (fAmt > fromBalance) { toast(`Insufficient ${fromCoin} balance`, 'error'); return }
        if (!rate) { toast('Rate unavailable, try again', 'error'); return }

        setSubmitting(true)
        await new Promise(r => setTimeout(r, 800))
        try {
            // Deduct from
            if (fromCoin === 'USDT') {
                applyLocalBalanceChange(-fAmt)
            } else {
                deductSpotHolding(fromCoin, fAmt)
            }
            // Credit to
            if (toCoin === 'USDT') {
                applyLocalBalanceChange(tAmt)
            } else {
                const usdValue = toCoin !== 'USDT' && rate
                    ? fAmt * (lastPrices[`${fromCoin}/USDT`] || lastPrices[`${fromCoin}USDT`] || 1)
                    : tAmt
                addSpotHolding(toCoin, tAmt, usdValue)
            }
            setLastResult({ fromAmt: fAmt, toAmt: tAmt, from: fromCoin, to: toCoin })
            setFromAmount(''); setToAmount('')
            setSuccess(true)
        } catch (e: any) {
            toast(e.message || 'Conversion failed', 'error')
        } finally { setSubmitting(false) }
    }

    const fAmt = parseFloat(fromAmount)
    const isValid = !!(fromAmount && fAmt > 0 && fAmt <= fromBalance && rate && toAmount)
    const pctFilled = fromBalance > 0 && fAmt > 0 ? Math.min((fAmt / fromBalance) * 100, 100) : 0

    const rateDisplay = rate
        ? `1 ${fromCoin} ≈ ${fmt(rate)} ${toCoin}`
        : rateLoading ? 'Fetching rate...' : 'Rate unavailable'

    // ── Success ───────────────────────────────────────────────────────────────
    if (success && lastResult) {
        return (
            <Layout activePage="assets" hideMobileNav={true} hideFooterMobile={true}>
                <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', gap: 28 }}>
                    <div style={{ width: 88, height: 88, position: 'relative' }}>
                        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(28,212,167,0.08)' }} />
                        <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', background: 'rgba(28,212,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircle size={38} color="#1CD4A7" strokeWidth={1.8} />
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Conversion Complete</div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--ui-text-primary)', letterSpacing: '-0.5px' }}>
                            {fmt(lastResult.toAmt)} {lastResult.to}
                        </div>
                    </div>
                    <div style={{ width: '100%', maxWidth: 380, background: 'var(--ui-bg-card)', borderRadius: 18, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
                        {[
                            ['Converted', `${fmt(lastResult.fromAmt)} ${lastResult.from}`],
                            ['Received',  `${fmt(lastResult.toAmt)} ${lastResult.to}`],
                            ['Rate',      `1 ${lastResult.from} ≈ ${rate ? fmt(rate) : ''} ${lastResult.to}`],
                            ['Fee',       'Free'],
                        ].map(([label, value], i, arr) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: i < arr.length - 1 ? '1px solid #1C1C2C' : 'none' }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>{label}</span>
                                <span style={{ color: label === 'Fee' ? '#1CD4A7' : label === 'Received' ? '#1CD4A7' : '#a1a1aa', fontSize: 13, fontWeight: 600 }}>{value}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 380 }}>
                        <button onClick={() => setSuccess(false)} style={{ background: '#1CD4A7', border: 'none', borderRadius: 14, padding: '16px', color: '#000', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Convert Again
                        </button>
                        <button onClick={() => navigate(-1)} style={{ background: 'transparent', border: '1px solid var(--ui-border)', borderRadius: 14, padding: '16px', color: 'var(--ui-text-muted)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Done
                        </button>
                    </div>
                </div>
            </Layout>
        )
    }

    // ── Main ──────────────────────────────────────────────────────────────────
    return (
        <Layout activePage="assets" hideMobileNav={true} hideFooterMobile={true}>
            <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 16px', borderBottom: '1px solid #1C1C2C' }}>
                    <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ui-text-primary)', flexShrink: 0 }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0 }}>Convert</h1>
                    <button onClick={() => fetchRate(fromCoin, toCoin)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--ui-text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6 }}>
                        <RefreshCw size={16} style={{ animation: rateLoading ? 'spin 1s linear infinite' : 'none' }} />
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

                    {/* From/To block */}
                    <div>
                        <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginBottom: 8, fontWeight: 500 }}>Convert</div>
                        <div style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 16, position: 'relative' }}>

                            {/* From */}
                            <div style={{ padding: '14px 16px' }}>
                                <div style={{ fontSize: 11, color: 'var(--ui-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 600, marginBottom: 10 }}>From</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    {/* Coin selector */}
                                    <button onClick={() => setPickerFor('from')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '8px 10px 8px 8px', cursor: 'pointer', flexShrink: 0 }}>
                                        <CoinLogo symbol={fromCoin} size={24} />
                                        <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>{fromCoin}</span>
                                        <ChevronDown size={13} color="#52526a" />
                                    </button>
                                    {/* Amount input */}
                                    <input
                                        type="number" inputMode="decimal" placeholder="0.00"
                                        value={fromAmount} onChange={e => handleFromChange(e.target.value)}
                                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--ui-text-primary)', fontSize: 22, fontWeight: 700, fontFamily: 'inherit', textAlign: 'right', minWidth: 0 }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                    <span style={{ color: 'var(--ui-text-secondary)', fontSize: 12 }}>Avail: <span style={{ color: 'var(--ui-text-muted)' }}>{fromCoin === 'USDT' && balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${fmt(fromBalance)} ${fromCoin}`}</span></span>
                                    <button onClick={() => setPercent(100)} style={{ background: 'rgba(28,212,167,0.1)', border: '1px solid rgba(28,212,167,0.2)', borderRadius: 5, color: '#1CD4A7', fontSize: 10, fontWeight: 700, padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.4px' }}>MAX</button>
                                </div>
                                {/* Progress bar */}
                                <div style={{ marginTop: 8, height: 2, background: 'var(--ui-bg-elevated)', borderRadius: 99 }}>
                                    <div style={{ height: '100%', width: `${pctFilled}%`, background: pctFilled > 90 ? '#EF4444' : '#1CD4A7', borderRadius: 99, transition: 'width 0.2s' }} />
                                </div>
                                {/* Percent buttons */}
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                    {[25, 50, 75, 100].map(p => (
                                        <button key={p} onClick={() => setPercent(p)} style={{ flex: 1, background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: 6, color: 'var(--ui-text-muted)', fontSize: 11, fontWeight: 600, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>
                                            {p}%
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Swap button */}
                            <div style={{ position: 'relative', height: 0, display: 'flex', justifyContent: 'center', zIndex: 10 }}>
                                <button onClick={handleSwap} style={{ position: 'absolute', top: -18, width: 36, height: 36, borderRadius: '50%', background: 'var(--ui-bg-primary)', border: '2px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1CD4A7', transform: swapping ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                                    <RefreshCw size={15} />
                                </button>
                            </div>

                            <div style={{ height: 1, background: 'var(--ui-bg-elevated)' }} />

                            {/* To */}
                            <div style={{ padding: '14px 16px' }}>
                                <div style={{ fontSize: 11, color: 'var(--ui-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 600, marginBottom: 10 }}>To</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button onClick={() => setPickerFor('to')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '8px 10px 8px 8px', cursor: 'pointer', flexShrink: 0 }}>
                                        <CoinLogo symbol={toCoin} size={24} />
                                        <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>{toCoin}</span>
                                        <ChevronDown size={13} color="#52526a" />
                                    </button>
                                    <input
                                        type="number" inputMode="decimal" placeholder="0.00"
                                        value={toAmount} onChange={e => handleToChange(e.target.value)}
                                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#1CD4A7', fontSize: 22, fontWeight: 700, fontFamily: 'inherit', textAlign: 'right', minWidth: 0 }}
                                    />
                                </div>
                                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ui-text-secondary)' }}>{rateDisplay}</div>
                            </div>
                        </div>
                    </div>

                    {/* Convert button */}
                    <button onClick={handleConvert} disabled={!isValid || submitting}
                        style={{ background: isValid ? '#1CD4A7' : '#1C1C2C', border: 'none', borderRadius: 14, padding: '16px', color: isValid ? '#000' : '#3a3a50', fontSize: 15, fontWeight: 700, width: '100%', cursor: isValid ? 'pointer' : 'not-allowed', transition: 'all 0.2s', fontFamily: 'inherit', opacity: submitting ? 0.7 : 1 }}>
                        {submitting ? 'Converting...' : fromAmount && !isValid && fAmt > fromBalance ? `Insufficient ${fromCoin}` : `Convert ${fromCoin} to ${toCoin}`}
                    </button>

                    {/* Info rows */}
                    <div style={{ background: 'var(--ui-bg-card)', borderRadius: 14, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
                        {[
                            ['Rate',             rateLoading ? 'Loading...' : rate ? `1 ${fromCoin} ≈ ${rate.toFixed(rate < 0.001 ? 8 : 4)} ${toCoin}` : 'N/A'],
                            ['Transaction Fee',  'Free'],
                            ['Processing Time',  'Instant'],
                        ].map(([label, value], i, arr) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: i < arr.length - 1 ? '1px solid #1C1C2C' : 'none' }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>{label}</span>
                                <span style={{ color: value === 'Free' ? '#1CD4A7' : '#a1a1aa', fontSize: 13, fontWeight: 600 }}>{value}</span>
                            </div>
                        ))}
                    </div>

                </div>

                {/* Coin Pickers */}
                <CoinPicker open={pickerFor === 'from'} onClose={() => setPickerFor(null)} exclude={toCoin} holdings={holdings}
                    onSelect={c => { setFromCoin(c); setFromAmount(''); setToAmount('') }} />
                <CoinPicker open={pickerFor === 'to'} onClose={() => setPickerFor(null)} exclude={fromCoin} holdings={holdings}
                    onSelect={c => { setToCoin(c); setToAmount('') }} />

                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
        </Layout>
    )
}
